import {
  ImageField as BaseImageField,
  InputField,
  GoogleMap,
  PlacesField,
  SelectField,
  Flex,
  FlexColumn,
  mapUtils,
  Header3SemiBold
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { required } from '@ubeya/shared/utils/form';
import React, { useContext, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import styled, { css } from 'styled-components';
import { Field, useField } from 'react-final-form';

const LocationModalFieldsRoot = styled(Flex)`
  width: 100%;
  flex-direction: column;
`;

const FormContainer = styled(Flex)`
  width: 100%;
  margin-bottom: 16px;

  > :not(style) ~ :not(style) {
    margin-left: 24px;
  }
`;

const InputsContainer = styled(FlexColumn)`
  width: 100%;
  flex: 1;

  > :not(style) ~ :not(style) {
    margin-top: 16px;
  }
`;

const ImageField = styled(BaseImageField)`
  min-height: 132px;
  max-height: 132px;
  border-radius: 0;
  width: 200px;
  margin-top: 22px;
  border-width: 1px;

  ${({ $isMobileOnly }) =>
    $isMobileOnly &&
    css`
      width: 100px;
      height: 100px;
    `}
`;

const Label = styled.div`
  color: ${({ theme }) => theme.colors.grey4};
  margin-bottom: 3px;
  font-size: 13rem;
  line-height: 20rem;
`;

const Warning = styled.div`
  margin-top: -10px;
  display: list-item;
  list-style-type: disc;
  list-style-position: inside;
`;

const Category = styled(Header3SemiBold)`
  background: ${({ theme }) => theme.colors.grey1};
  letter-spacing: 2.25px;
  color: ${({ theme }) => theme.colors.black};
  margin: 0px -10px 15px -10px;
  padding: 4px 13px;
`;

const LocationModalFields = ({
  selectedLocation,
  initialCenterLocation,
  showBranchSelector = false,
  editableAddress = false,
  disableFieldsEdit = false,
  disableBranchEdit = false,
  optionalAddress = false,
  branchesOptions = [],
  showPhoto = true,
  namePrefix = '',
  afterNameField = null
}) => {
  const { t } = useContext(ConfigContext);

  const {
    input: { value: locationValue, onChange: setLocation }
  } = useField('location');

  const {
    input: { onChange: setAddress }
  } = useField('address');

  const [centerPosition, setCenterPosition] = useState({
    lat: initialCenterLocation?.lat || 0,
    lng: initialCenterLocation?.lng || 0
  });

  // keep the map on whatever the form's location holds, so coordinates set from outside this
  // component (picking a venue, which inherits its address) recenter it too
  useEffect(() => {
    const { lat, lng } = locationValue || {};
    if (!lat || !lng) {
      return;
    }
    setCenterPosition((current) => (current.lat === lat && current.lng === lng ? current : { lat, lng }));
  }, [locationValue]);

  const disableEdit = !!selectedLocation?.id && disableFieldsEdit;
  const disableBranches = !!disableEdit || disableBranchEdit;
  const disablePlaces = !!disableEdit || (!!selectedLocation?.id && !editableAddress);

  // upon pin drop on the map directly
  const onMapChange = async (mapProps, _, event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    const address = await mapUtils.getAddressFromLatLng(mapProps.google, lat, lng);
    setAddress(address);
    setLocation({ lat, lng });
  };

  const onPlaceChange = ({ lat, lng }) => {
    setCenterPosition({ lat, lng });
  };

  return (
    <LocationModalFieldsRoot>
      <Category>{t('locationDetails')}</Category>
      <FormContainer>
        <InputsContainer>
          {showBranchSelector && (
            <SelectField
              title={t('branchName')}
              options={branchesOptions}
              autoFocus
              name={`${namePrefix}branchId`}
              required
              isMulti={false}
              isDisabled={disableBranches}
            />
          )}
          <InputField
            name="name"
            title={t('name')}
            autoFocus={!showBranchSelector}
            validate={required}
            isDisabled={disableEdit}
          />
          {afterNameField}
          <PlacesField
            name="address"
            title={`${t('address')} ${optionalAddress ? `(${t('optional')})` : ''}`}
            validate={!optionalAddress && required}
            locationName="location"
            disabled={disablePlaces}
            usePortal
            onPlaceChange={onPlaceChange}
          />
          {!disablePlaces ? <Warning>{t('pleaseSelectFromTheListAbove')}</Warning> : null}
        </InputsContainer>
        {showPhoto && (
          <ImageField
            name="photo"
            $isMobileOnly={isMobileOnly}
            $showBranchSelector={showBranchSelector}
            enabled={!disableEdit}
          />
        )}
      </FormContainer>
      <FlexColumn style={{ display: centerPosition?.lat ? 'block' : 'none' }}>
        <Label>{t('choosePerciseLocation')}</Label>
        <Field
          name="location"
          render={({ input }) => (
            <GoogleMap
              center={centerPosition}
              initialCenter={centerPosition}
              markers={[input.value || centerPosition]}
              onClick={disablePlaces ? undefined : onMapChange}
              zoom={16}
              fullscreenControl={false}
              mapTypeControl={false}
              streetViewControl={false}
              containerStyle={{ width: '100%', height: 220, position: 'relative' }}
            />
          )}
        />
      </FlexColumn>
    </LocationModalFieldsRoot>
  );
};

export default LocationModalFields;
